import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { type SortingState, useTable } from '@tanstack/react-table';
import { PlusIcon, SearchIcon, UsersIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { RoleType } from '@/common/permission';
import { ROLE } from '@/common/role';
import type { User } from '@/common/user';
import { AbsoluteLoader } from '@/components/absoluteLoader';
import { DataTable } from '@/components/data-table/data-table';
import { toSortParam } from '@/components/data-table/sort-param';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { usePermission } from '@/hooks/use-permission';
import { getListOrganizationsOption } from '@/queries/organizations/list-organizations';
import { getListRolesOption } from '@/queries/permissions/list-roles';
import { getListUsersOptions } from '@/queries/users/listUsers';
import { updateUserOptions } from '@/queries/users/update-user';
import { SelectOrganizationPopover } from '../../Organizations/SelectOrganizationPopup';
import { USER_SORT_FIELDS, userColumns, userTableFeatures } from './columns';
import { PasswordManagementDialog } from './PasswordManagementDialog';
import { UserDrawer } from './UserDrawer';

export function UsersTab() {
	const { t } = useTranslation();
	const [searchQuery, setSearchQuery] = useState('');
	const [organizationFilter, setOrganizationFilter] = useState<string>('all');
	const [isUserDrawerOpen, setIsUserDrawerOpen] = useState(false);
	const [selectedUser, setSelectedUser] = useState<User | null>(null);
	const [isPasswordManagementDialogOpen, setIsPasswordManagementDialogOpen] = useState(false);
	const [sorting, setSorting] = useState<SortingState>([]);
	const hasOrgPermission = usePermission(ROLE.ORGANIZATION_READ);

	const { data: orgListResponse } = useQuery(getListOrganizationsOption({ enabled: hasOrgPermission }));
	const {
		data: userListResponse,
		refetch,
		isLoading,
		isRefetching,
	} = useQuery({
		...getListUsersOptions({ limit: 1000, offset: 0, sort: toSortParam(sorting, USER_SORT_FIELDS) }),
		// Keep the current rows on screen while a new sort order loads.
		placeholderData: keepPreviousData,
	});
	const { data: roleListResponse } = useQuery(getListRolesOption());
	const roles = roleListResponse?.roles || [];
	const rolesById = roles.reduce(
		(acc, role) => {
			acc[role.id as string] = role;
			return acc;
		},
		{} as Record<string, RoleType>,
	);
	const organizations = orgListResponse?.organizations || [];
	const apiUsers = userListResponse?.users || [];

	const updateUserApi = useMutation(updateUserOptions());

	// Filter users
	const filteredUsers = useMemo(
		() =>
			apiUsers.filter((user) => {
				const matchesSearch =
					user.firstName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
					user.lastName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
					user.email?.toLowerCase().includes(searchQuery.toLowerCase());
				const matchesOrganization =
					organizationFilter === 'all' || user.organizations.findIndex((org) => org.id === organizationFilter) !== -1;
				return matchesSearch && matchesOrganization;
			}),
		[apiUsers, searchQuery, organizationFilter],
	);

	const handleCloseDrawer = () => {
		setIsUserDrawerOpen(false);
		setSelectedUser(null);
	};

	const handleToggleStatus = async (user: User) => {
		await updateUserApi.mutateAsync({
			id: user.id,
			payload: { enabled: !user.enabled },
		});
		refetch();
	};

	const handleUserChange = () => {
		refetch();
	};

	const table = useTable({
		features: userTableFeatures,
		columns: userColumns,
		data: filteredUsers,
		getRowId: (row) => row.id,
		state: { sorting },
		onSortingChange: setSorting,
		// The API sorts the whole list; search and organization filters then narrow it client-side.
		manualSorting: true,
		// Skip auto-detecting the first direction from row values; always start ascending.
		sortDescFirst: false,
		meta: {
			rolesById,
			onToggleStatus: handleToggleStatus,
			onEdit: (user) => {
				setSelectedUser(user);
				setIsUserDrawerOpen(true);
			},
			onManagePassword: (user) => {
				setSelectedUser(user);
				setIsPasswordManagementDialogOpen(true);
			},
		},
	});

	const showLoader = isLoading || isRefetching || updateUserApi.isPending;

	return (
		<>
			<div className="space-y-4">
				{/* Search and Filters */}
				<div className="flex items-center gap-4 flex-wrap">
					<div className="relative flex-1 min-w-75">
						<SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
						<Input
							type="text"
							placeholder={t('admin.users.searchPlaceholder')}
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
							className="w-full pl-10 pr-4 py-2 bg-input"
						/>
					</div>

					{hasOrgPermission && (
						<div className="relative w-auto min-w-70 h-auto">
							<SelectOrganizationPopover
								asFilter
								addAllItem
								organizations={organizations}
								onChange={(value) => setOrganizationFilter(value)}
								value={organizationFilter}
							/>
						</div>
					)}

					<Button onClick={() => setIsUserDrawerOpen(true)} className="ml-auto">
						<PlusIcon className="size-4 mr-2" />
						{t('admin.users.create')}
					</Button>
				</div>
				<div className="relative">
					<AbsoluteLoader isLoading={showLoader} />
					{/* Users Table */}
					{!isLoading && !!filteredUsers.length && <DataTable table={table} />}
					{!isLoading && !filteredUsers.length && (
						<Card>
							<CardContent className="p-12">
								<div className="text-center">
									<div className="mx-auto h-12 w-12 rounded-full flex items-center justify-center mb-4">
										<UsersIcon className="h-6 w-6 text-muted-foreground" />
									</div>
									<h3 className="text-lg font-semibold mb-2">{t('admin.users.empty.title')}</h3>
									<p className="text-sm text-muted-foreground mb-4">{t('admin.users.empty.searchHint')}</p>
								</div>
							</CardContent>
						</Card>
					)}
				</div>
			</div>

			<UserDrawer
				open={isUserDrawerOpen}
				onOpenChange={handleCloseDrawer}
				user={selectedUser}
				onUserChange={handleUserChange}
			/>

			<PasswordManagementDialog
				isOpen={isPasswordManagementDialogOpen}
				onClose={() => {
					setIsPasswordManagementDialogOpen(false);
					setSelectedUser(null);
				}}
				user={selectedUser}
			/>
		</>
	);
}
